import React from 'react';
import { Box, Paper, Typography, Chip, Stack, Link } from '@mui/material';
import { Warning as WarnIcon } from '@mui/icons-material';
import { TrainingInsights, scoreColor } from '../types';
import SectionShell from './SectionShell';

interface Props {
  data: TrainingInsights['landmineHandling'];
  onNavigateToTimestamp?: (timestampIso: string) => void;
}

const LandmineHandlingSection: React.FC<Props> = ({ data, onNavigateToTimestamp }) => {
  if (!data) return null;

  return (
    <SectionShell title="Landmine Handling" score={data.overallScore ?? null} summary={data.overallSummary}>

      <Stack spacing={2}>
        {data.items?.map((item, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <WarnIcon
                color={item.wasTriggered ? 'error' : 'success'}
                fontSize="small"
              />
              <Typography variant="subtitle2">
                {item.trigger}
              </Typography>
              <Chip
                label={item.wasTriggered ? 'triggered' : 'avoided'}
                size="small"
                color={item.wasTriggered ? 'error' : 'success'}
                variant="outlined"
              />
              <Chip
                label={`${item.handlingScore}%`}
                size="small"
                color={scoreColor(item.handlingScore)}
              />
              {item.timestampIso && onNavigateToTimestamp && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => onNavigateToTimestamp(item.timestampIso!)}
                  sx={{ ml: 'auto' }}
                >
                  View in timeline &rarr;
                </Link>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {item.handlingSummary}
            </Typography>
            {item.whatRepSaid && (
              <Typography variant="caption" display="block" color="text.secondary">
                <strong>Rep said:</strong> "{item.whatRepSaid}"
              </Typography>
            )}
            {item.recommendedResponse && (
              <Box mt={1} p={1.5} bgcolor="var(--info-tint)" borderRadius={1}>
                <Typography variant="caption" color="info.main" fontWeight={600}>
                  Recommended response
                </Typography>
                <Typography variant="body2">{item.recommendedResponse}</Typography>
              </Box>
            )}
          </Paper>
        ))}
      </Stack>
    </SectionShell>
  );
};

export default LandmineHandlingSection;
